import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Anime } from "@/types";

export default function HeroCarousel({ items }: { items: Anime[] }) {
  const [index, setIndex] = useState(0);

  const go = useCallback(
    (next: number) => setIndex((next + items.length) % items.length),
    [items.length]
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  const current = items[index];
  if (!current) return null;

  return (
    <section className="relative w-full h-[460px] md:h-[600px] overflow-hidden bg-foreground-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.banner}
            alt={`${item.title} 焦点剧照`}
            title={`${item.title} 热门动画推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/45 to-foreground-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-6 flex items-center">
        <div className="max-w-2xl animate-fade-up" key={current.id}>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
            <i className="ri-fire-fill" />
            本周焦点推荐
          </span>
          <h1 className="mt-5 font-heading text-3xl md:text-5xl font-extrabold text-background-50 leading-tight">
            {current.title}
          </h1>
          <p className="mt-2 text-sm md:text-base text-background-200">{current.titleJa}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-background-100">
            <span className="flex items-center gap-1 text-accent-300 font-semibold">
              <i className="ri-star-fill" />
              {current.rating.toFixed(1)}
            </span>
            <span className="hidden sm:inline text-background-300">|</span>
            <span>{current.year} 年</span>
            <span className="hidden sm:inline text-background-300">|</span>
            <span>{current.episodes} 集</span>
            <span className="hidden sm:inline text-background-300">|</span>
            <span>{current.status}</span>
          </div>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-background-200 line-clamp-2 md:line-clamp-3 max-w-xl">
            {current.synopsis}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg" />
              查看动画详情
            </Link>
            <Link
              to="/category/all"
              className="inline-flex items-center gap-2 px-6 h-11 rounded-md border border-background-300/70 text-background-100 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
            >
              浏览全部资源
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到 ${item.title}`}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-primary-500" : "w-2 bg-background-50/60 hover:bg-background-50"
            }`}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background-50/20 backdrop-blur text-background-50 items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-background-50/20 backdrop-blur text-background-50 items-center justify-center hover:bg-background-50/35 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>
    </section>
  );
}